import { createEnum } from '../helpers'
import { YEAR_FLAGS, type YearFlag } from '../time'
import { PRODUCT_CATEGORY, type ProductCategory } from './materials'

export const CONTRACT_CLAUSE = {
  CONDITIONAL: 'Conditional',
  P4P: 'P4P', // Pay for Performance
  SLA: 'SLA', // Store Level Activation
} as const

export type ContractClause = (typeof CONTRACT_CLAUSE)[keyof typeof CONTRACT_CLAUSE]

export const CONTRACT_CLAUSES = Object.values(CONTRACT_CLAUSE) as unknown as readonly [
  ContractClause,
  ...ContractClause[],
]

/**
 * Whether a key account has a contract estimate for the period — the split the key-accounts tracker
 * filters on and the P&L key-account lines break down by.
 */
export const KEY_ACCOUNT_ESTIMATE_STATUS = {
  WITH_ESTIMATE: 'withEstimate',
  WITHOUT_ESTIMATE: 'withoutEstimate',
} as const

export type KeyAccountEstimateStatus =
  (typeof KEY_ACCOUNT_ESTIMATE_STATUS)[keyof typeof KEY_ACCOUNT_ESTIMATE_STATUS]

export const KEY_ACCOUNT_ESTIMATE_STATUSES = Object.values(
  KEY_ACCOUNT_ESTIMATE_STATUS,
) as unknown as readonly [KeyAccountEstimateStatus, ...KeyAccountEstimateStatus[]]

// Human-readable labels for each contract clause code, in every supported
// language. Kept in sync with the i18n keys
// `keyAccountContractEstimates.contractClause.*` in en/fr crt.json. Used so that
// imports can accept either the stored code (e.g. "P4P") or the label the user
// sees in the UI (e.g. "Performance").
export const CONTRACT_CLAUSE_LABELS: Record<ContractClause, readonly string[]> = {
  [CONTRACT_CLAUSE.CONDITIONAL]: ['Conditional', 'Conditionnel'],
  [CONTRACT_CLAUSE.P4P]: ['Performance'],
  [CONTRACT_CLAUSE.SLA]: ['Contract SLA', 'SLA contractuel'],
}

// Case-insensitive, whitespace-trimmed lookup from either a clause code or one of
// its labels to the canonical code.
const CONTRACT_CLAUSE_LOOKUP = new Map<string, ContractClause>()
for (const code of CONTRACT_CLAUSES) {
  CONTRACT_CLAUSE_LOOKUP.set(code.toLowerCase(), code)
}
for (const [code, labels] of Object.entries(CONTRACT_CLAUSE_LABELS) as [
  ContractClause,
  readonly string[],
][]) {
  for (const label of labels) {
    CONTRACT_CLAUSE_LOOKUP.set(label.toLowerCase(), code)
  }
}

/**
 * Resolve an imported contract clause value to its canonical code. Accepts both
 * the stored code (e.g. "P4P") and the human-readable label (e.g. "Performance"),
 * case-insensitively. Returns null when the value matches neither.
 */
export const resolveContractClause = (value: string): ContractClause | null => {
  return CONTRACT_CLAUSE_LOOKUP.get(value.trim().toLowerCase()) ?? null
}

// Stable keys (not display prose) for the investment-type controlled vocabulary. Stored in the
// `investment_type` columns and used as i18n keys (`keyAccountContractEstimates.investmentType.*`),
// so the human-readable label lives only in the translation files and can change without a data
// migration.
//
// Only types a write path can produce belong here: the seeded vistex condition types, the GO master
// agreement mapping, and the contract clause conversion below. SLA is a contract clause that converts
// to MI in GTO, not an investment type of its own.
export const INVESTMENT_TYPE = {
  SALES_DEALS: 'salesDeals',
  COMMERCIAL_FUND: 'commercialFund',
  P4P: 'tradePayForPerformance',
  GO: 'go', // (AKA: Extra) P4P in independant retailers
  MI_IN_GTO: 'miInGto',
} as const

export type InvestmentType = (typeof INVESTMENT_TYPE)[keyof typeof INVESTMENT_TYPE]

export const INVESTMENT_TYPES = Object.values(INVESTMENT_TYPE) as readonly InvestmentType[]

// English display labels for the server-side CSV exports ONLY. The backend has no i18n and those CSVs
// already emit English headers, so the label has to be resolved here. Keep in sync with the
// `keyAccountContractEstimates.investmentType.*` values in en/crt.json.
// Do NOT use this in the frontend: there, always translate the key instead (see
// `getInvestmentTypeLabel` in apps/frontend/src/lib/utils/crt.ts).
export const INVESTMENT_TYPE_EXPORT_LABELS: Record<InvestmentType, string> = {
  [INVESTMENT_TYPE.SALES_DEALS]: 'Sales Deals',
  [INVESTMENT_TYPE.COMMERCIAL_FUND]: 'Commercial Fund',
  [INVESTMENT_TYPE.P4P]: 'Trade Pay For Performance',
  [INVESTMENT_TYPE.GO]: 'GO (Extra)',
  [INVESTMENT_TYPE.MI_IN_GTO]: 'MI in GTO',
}

/**
 * Resolve an investment type key to its English label for a CSV export. Unknown keys fall through
 * unchanged so an export never silently loses data.
 */
export const getInvestmentTypeExportLabel = (value: string | null) =>
  value ? (INVESTMENT_TYPE_EXPORT_LABELS[value as InvestmentType] ?? value) : null

export const CONTRACT_CLAUSE_TO_INVESTMENT_TYPE: Record<ContractClause, InvestmentType> = {
  [CONTRACT_CLAUSE.CONDITIONAL]: INVESTMENT_TYPE.MI_IN_GTO,
  [CONTRACT_CLAUSE.SLA]: INVESTMENT_TYPE.MI_IN_GTO,
  [CONTRACT_CLAUSE.P4P]: INVESTMENT_TYPE.P4P,
}

export const getInvestmentTypeForContractClause = (
  contractClause: ContractClause,
): InvestmentType => {
  return CONTRACT_CLAUSE_TO_INVESTMENT_TYPE[contractClause]
}

export const AGREEMENT_MAPPINGS_IMPORT_ERRORS = [
  'zodSchemaError',
  'unknownMasterAgreement',
  'notKeyAccount',
  'invalidContractClause',
] as const

export const AGREEMENT_MAPPINGS_IMPORT_ERROR_TYPES = createEnum(AGREEMENT_MAPPINGS_IMPORT_ERRORS)

export type AgreementMappingsImportErrorType = (typeof AGREEMENT_MAPPINGS_IMPORT_ERRORS)[number]

export const KEY_ACCOUNT_ESTIMATES_IMPORT_ERRORS = [
  'zodSchemaError',
  'unknownHierarchyErp',
] as const

export const KEY_ACCOUNT_ESTIMATES_IMPORT_ERROR_TYPES = createEnum(
  KEY_ACCOUNT_ESTIMATES_IMPORT_ERRORS,
)

export type KeyAccountEstimatesImportErrorType =
  (typeof KEY_ACCOUNT_ESTIMATES_IMPORT_ERRORS)[number]

export const IMPORTED_INVESTMENT_SOURCES = [
  'sales-deals-actuals',
  'ip-accruals',
  'calculation-run-accruals',
] as const

export type ImportedInvestmentSource = (typeof IMPORTED_INVESTMENT_SOURCES)[number]

export const INVESTMENT_SOURCES = [...IMPORTED_INVESTMENT_SOURCES, 'manual-investments'] as const

export type InvestmentSource = (typeof INVESTMENT_SOURCES)[number]

// User-facing grouping of investment sources, used as a single-select filter on the
// customer-investments page (each category fans out to one or more sources).
export const INVESTMENT_CATEGORIES = ['Sales deals', 'Accruals', 'Manual'] as const

export type InvestmentCategory = (typeof INVESTMENT_CATEGORIES)[number]

export const INVESTMENT_CATEGORY_TO_SOURCES: Record<
  InvestmentCategory,
  readonly InvestmentSource[]
> = {
  'Sales deals': ['sales-deals-actuals'],
  Accruals: ['ip-accruals', 'calculation-run-accruals'],
  Manual: ['manual-investments'],
}

export const getInvestmentSourcesByCategory = (
  category: InvestmentCategory,
): readonly InvestmentSource[] => INVESTMENT_CATEGORY_TO_SOURCES[category]

export const BUDGET_CATEGORIES = ['FMC', 'VAPOUR', 'NRT', 'KEY_ACCOUNTS'] as const

export type BudgetCategory = (typeof BUDGET_CATEGORIES)[number]

// Which calendar year of a CRT period a budget estimate targets. Stored on `ci_budget_estimates`.
export const BUDGET_ESTIMATE_YEAR_SCOPES = ['current_year', 'next_year'] as const

export type BudgetEstimateYearScope = (typeof BUDGET_ESTIMATE_YEAR_SCOPES)[number]

// Maps the `CY`/`NY` year flag (shared with the P&L view) to the stored year scope.
export const yearFlagToBudgetEstimateScope = (yearFlag: YearFlag): BudgetEstimateYearScope =>
  yearFlag === YEAR_FLAGS.NY ? 'next_year' : 'current_year'

export const BUDGET_CATEGORY_PREFIXES: Record<BudgetCategory, readonly string[]> = {
  FMC: ['F'],
  VAPOUR: ['V'],
  NRT: ['M'],
  KEY_ACCOUNTS: ['K', 'E'],
}

/**
 * A budget allocation belongs to a Key Account when its code starts with one of
 * the `KEY_ACCOUNTS` prefixes (E or K). Used to restrict master-agreement
 * mapping export/import to Key Accounts.
 */
export function isKeyAccountBudget(budgetAllocation: string | null | undefined): boolean {
  if (!budgetAllocation) return false
  const first = budgetAllocation.charAt(0).toUpperCase()
  return BUDGET_CATEGORY_PREFIXES.KEY_ACCOUNTS.includes(first)
}

// The inverse of `BUDGET_CATEGORY_PREFIXES`: prefix letter → its budget category, built once.
const BUDGET_CATEGORY_BY_PREFIX: Record<string, BudgetCategory> = Object.fromEntries(
  BUDGET_CATEGORIES.flatMap((category) =>
    BUDGET_CATEGORY_PREFIXES[category].map((prefix) => [prefix, category]),
  ),
)

/**
 * The budget category a code belongs to, resolved from its first letter — the inverse of
 * {@link BUDGET_CATEGORY_PREFIXES}. `null` when the code is empty or its prefix is unknown.
 */
export function getBudgetCategory(
  budgetAllocation: string | null | undefined,
): BudgetCategory | null {
  if (!budgetAllocation) return null
  const first = budgetAllocation.charAt(0).toUpperCase()
  return first ? (BUDGET_CATEGORY_BY_PREFIX[first] ?? null) : null
}

/**
 * The product category a budget category's spend applies to. `KEY_ACCOUNTS` budgets span every product
 * category (they are keyed by customer, not by product), so they map to `null` — consumers that need to
 * attribute a budget to materials skip those buckets rather than guess a category.
 */
export const BUDGET_CATEGORY_TO_PRODUCT_CATEGORY = {
  FMC: PRODUCT_CATEGORY.FMC,
  VAPOUR: PRODUCT_CATEGORY.VAPOUR,
  NRT: PRODUCT_CATEGORY.NRT,
  KEY_ACCOUNTS: null,
} as const satisfies Record<BudgetCategory, ProductCategory | null>

export const AGREEMENT_IMPORT_ERRORS = ['zodSchemaError'] as const

export const AGREEMENT_IMPORT_ERROR_TYPES = createEnum(AGREEMENT_IMPORT_ERRORS)
export type AgreementImportErrorType = (typeof AGREEMENT_IMPORT_ERRORS)[number]

export const MARGINS_IMPORT_ERRORS = [
  'zodSchemaError',
  'invalidBrand',
  'invalidProductType',
  'invalidSubChannel',
  'invalidPackContentSize',
] as const

export const MARGINS_IMPORT_ERROR_TYPES = createEnum(MARGINS_IMPORT_ERRORS)
export type MarginsImportErrorType = (typeof MARGINS_IMPORT_ERRORS)[number]

/**
 * The material pack content sizes each FMC margin grid row governs. The grids only ever store rows at 20
 * and 25, but a 40-pack is priced as two 20s and a 50 as two 25s, so a ratio governs its double as well
 * as itself.
 *
 * Read-side only: a *stored* row may still only be keyed 20 or 25, which is what the CSV import enforces.
 * The coverage sets are disjoint, so a material still resolves at most one grid row per margin table.
 */
export const FMC_MARGIN_PACK_CONTENT_SIZE_COVERAGE = {
  20: [20, 40],
  25: [25, 50],
} as const satisfies Record<number, readonly number[]>

/** A pack content size an FMC margin grid row may be keyed by. */
export type FmcMarginPackContentSize = keyof typeof FMC_MARGIN_PACK_CONTENT_SIZE_COVERAGE

/** The sizes the FMC margin grids store rows at, in the order the pages list them. */
export const FMC_MARGIN_GRID_PACK_CONTENT_SIZES = [
  20, 25,
] as const satisfies readonly FmcMarginPackContentSize[]

/**
 * The material pack content sizes a grid row at `packContentSize` governs — empty when the grid holds no
 * row at that size, so a caller reads "covers nothing" rather than "covers itself".
 */
export const getFmcMarginPackContentSizes = (packContentSize: number): readonly number[] =>
  FMC_MARGIN_PACK_CONTENT_SIZE_COVERAGE[packContentSize as FmcMarginPackContentSize] ?? []

export const RAP_FORECASTS_IMPORT_ERRORS = ['zodSchemaError'] as const

export const RAP_FORECASTS_IMPORT_ERROR_TYPES = createEnum(RAP_FORECASTS_IMPORT_ERRORS)
export type RapForecastsImportErrorType = (typeof RAP_FORECASTS_IMPORT_ERRORS)[number]

export const RAP_FORECASTS_CREATE_ERRORS = ['crtPeriodConflict'] as const

export const RAP_FORECASTS_CREATE_ERROR_TYPES = createEnum(RAP_FORECASTS_CREATE_ERRORS)
export type RapForecastsCreateErrorType = (typeof RAP_FORECASTS_CREATE_ERRORS)[number]

export const RAP_FORECASTS_CREATE_CONSTRAINT_TO_ERROR: Record<string, RapForecastsCreateErrorType> =
  {
    rap_forecasts_crt_period_unique: RAP_FORECASTS_CREATE_ERROR_TYPES.crtPeriodConflict,
  }

export const SALES_DEAL_ACTUALS_IMPORT_ERRORS = ['zodSchemaError', 'periodMismatch'] as const

export const SALES_DEAL_ACTUALS_IMPORT_ERROR_TYPES = createEnum(SALES_DEAL_ACTUALS_IMPORT_ERRORS)
export type SalesDealActualsImportErrorType = (typeof SALES_DEAL_ACTUALS_IMPORT_ERRORS)[number]

export const SALES_DEAL_ACTUALS_CREATE_ERRORS = ['periodConflict'] as const

export const SALES_DEAL_ACTUALS_CREATE_ERROR_TYPES = createEnum(SALES_DEAL_ACTUALS_CREATE_ERRORS)
export type SalesDealActualsCreateErrorType = (typeof SALES_DEAL_ACTUALS_CREATE_ERRORS)[number]

export const SALES_DEAL_ACTUALS_CREATE_CONSTRAINT_TO_ERROR: Record<
  string,
  SalesDealActualsCreateErrorType
> = {
  sales_deal_actuals_period_unique: SALES_DEAL_ACTUALS_CREATE_ERROR_TYPES.periodConflict,
}

export const UNKNOWN_AGREEMENT_CODE = 'UNKNWN'

// The distribution margin mix `condition_rate` is a dollar amount expressed per KAR
// (a case of 200 units). The business wants it on a per-1000 (TH) basis, so it is
// multiplied by TH / KAR = 1000 / 200 = 5. This is hardcoded because the imported data
// is expected to always be in KAR; rows with a different unit_of_measure are flagged in
// the UI rather than silently converted.
export const MARGIN_MIX_KAR_MULTIPLIER = 5
export const MARGIN_MIX_EXPECTED_UNIT_OF_MEASURE = 'KAR'

export const DISTRIBUTION_MARGIN_MIX_IMPORT_ERRORS = ['zodSchemaError'] as const

export const DISTRIBUTION_MARGIN_MIX_IMPORT_ERROR_TYPES = createEnum(
  DISTRIBUTION_MARGIN_MIX_IMPORT_ERRORS,
)
export type DistributionMarginMixImportErrorType =
  (typeof DISTRIBUTION_MARGIN_MIX_IMPORT_ERRORS)[number]

export const DISTRIBUTION_MARGIN_MIX_CREATE_ERRORS = ['crtPeriodConflict'] as const

export const DISTRIBUTION_MARGIN_MIX_CREATE_ERROR_TYPES = createEnum(
  DISTRIBUTION_MARGIN_MIX_CREATE_ERRORS,
)
export type DistributionMarginMixCreateErrorType =
  (typeof DISTRIBUTION_MARGIN_MIX_CREATE_ERRORS)[number]

export const DISTRIBUTION_MARGIN_MIX_CREATE_CONSTRAINT_TO_ERROR: Record<
  string,
  DistributionMarginMixCreateErrorType
> = {
  distribution_margin_mix_crt_period_unique:
    DISTRIBUTION_MARGIN_MIX_CREATE_ERROR_TYPES.crtPeriodConflict,
}

export const BUDGET_ALLOCATIONS_IMPORT_ERRORS = ['zodSchemaError'] as const

export const BUDGET_ALLOCATIONS_IMPORT_ERROR_TYPES = createEnum(BUDGET_ALLOCATIONS_IMPORT_ERRORS)
export type BudgetAllocationsImportErrorType = (typeof BUDGET_ALLOCATIONS_IMPORT_ERRORS)[number]

export const BUDGET_ALLOCATIONS_CREATE_ERRORS = ['budgetAllocationConflict'] as const

export const BUDGET_ALLOCATIONS_CREATE_ERROR_TYPES = createEnum(BUDGET_ALLOCATIONS_CREATE_ERRORS)
export type BudgetAllocationsCreateErrorType = (typeof BUDGET_ALLOCATIONS_CREATE_ERRORS)[number]

export const BUDGET_ALLOCATIONS_CREATE_CONSTRAINT_TO_ERROR: Record<
  string,
  BudgetAllocationsCreateErrorType
> = {
  budget_allocations_budget_allocation_unique:
    BUDGET_ALLOCATIONS_CREATE_ERROR_TYPES.budgetAllocationConflict,
}

export const IP_ACCRUALS_IMPORT_ERRORS = ['zodSchemaError', 'periodMismatch'] as const

export const IP_ACCRUALS_IMPORT_ERROR_TYPES = createEnum(IP_ACCRUALS_IMPORT_ERRORS)
export type IpAccrualsImportErrorType = (typeof IP_ACCRUALS_IMPORT_ERRORS)[number]

export const IP_ACCRUALS_CREATE_ERRORS = ['periodConflict'] as const

export const IP_ACCRUALS_CREATE_ERROR_TYPES = createEnum(IP_ACCRUALS_CREATE_ERRORS)
export type IpAccrualsCreateErrorType = (typeof IP_ACCRUALS_CREATE_ERRORS)[number]

export const IP_ACCRUALS_CREATE_CONSTRAINT_TO_ERROR: Record<string, IpAccrualsCreateErrorType> = {
  ip_accruals_period_unique: IP_ACCRUALS_CREATE_ERROR_TYPES.periodConflict,
}

export const CALCULATION_RUN_ACCRUALS_IMPORT_ERRORS = ['zodSchemaError', 'periodMismatch'] as const

export const CALCULATION_RUN_ACCRUALS_IMPORT_ERROR_TYPES = createEnum(
  CALCULATION_RUN_ACCRUALS_IMPORT_ERRORS,
)
export type CalculationRunAccrualsImportErrorType =
  (typeof CALCULATION_RUN_ACCRUALS_IMPORT_ERRORS)[number]

export const CALCULATION_RUN_ACCRUALS_CREATE_ERRORS = ['periodConflict'] as const

export const CALCULATION_RUN_ACCRUALS_CREATE_ERROR_TYPES = createEnum(
  CALCULATION_RUN_ACCRUALS_CREATE_ERRORS,
)
export type CalculationRunAccrualsCreateErrorType =
  (typeof CALCULATION_RUN_ACCRUALS_CREATE_ERRORS)[number]

export const CALCULATION_RUN_ACCRUALS_CREATE_CONSTRAINT_TO_ERROR: Record<
  string,
  CalculationRunAccrualsCreateErrorType
> = {
  calculation_run_accruals_period_unique:
    CALCULATION_RUN_ACCRUALS_CREATE_ERROR_TYPES.periodConflict,
}

export const MANUAL_INVESTMENT_ACCOUNT_CATEGORIES = {
  ALL_COMMERCIAL_PARTNERS: 'Commercial Partner',
  KEY_ACCOUNT: 'Key Account',
  HEAD_OFFICE: 'Head Office',
} as const

export type ManualInvestmentAccountCategory =
  (typeof MANUAL_INVESTMENT_ACCOUNT_CATEGORIES)[keyof typeof MANUAL_INVESTMENT_ACCOUNT_CATEGORIES]

export const MANUAL_INVESTMENT_ACCOUNT_CATEGORY_VALUES = Object.values(
  MANUAL_INVESTMENT_ACCOUNT_CATEGORIES,
) as readonly ManualInvestmentAccountCategory[]

export const MANUAL_INVESTMENT_PRODUCT_TARGETS_CREATE_ERRORS = ['noMatchingMaterials'] as const

export const MANUAL_INVESTMENT_PRODUCT_TARGETS_CREATE_ERROR_TYPES = createEnum(
  MANUAL_INVESTMENT_PRODUCT_TARGETS_CREATE_ERRORS,
)
export type ManualInvestmentProductTargetsCreateErrorType =
  (typeof MANUAL_INVESTMENT_PRODUCT_TARGETS_CREATE_ERRORS)[number]
